import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, MapPin, Clock, Users, CreditCard, Phone, Mail, 
  ChevronDown, ChevronUp, ExternalLink, Printer, Check, 
  Building, Plane, Car, HelpCircle, DollarSign, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { eventConfig } from '../data/eventConfig';

// Print-friendly schedule styles
const printStyles = `
@media print {
  .no-print { display: none !important; }
  .print-only { display: block !important; }
  .schedule-section { break-inside: avoid; page-break-inside: avoid; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
`;

const Events = () => {
  const [expandedDays, setExpandedDays] = useState({});
  const [expandedFaq, setExpandedFaq] = useState({});
  const [daysUntilEvent, setDaysUntilEvent] = useState(null);
  const [currentTier, setCurrentTier] = useState(null);

  const { event, dates, venue, hotel, travel, registration, payment, schedule, faq, contact, images } = eventConfig;

  // Calculate days until event and current pricing tier
  useEffect(() => {
    const eventDate = new Date(dates.start);
    const today = new Date();
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    setDaysUntilEvent(diffDays);

    // Determine current pricing tier
    const todayStr = today.toISOString().split('T')[0];
    const tier = registration.tiers.find(t => todayStr <= t.endDate) || registration.tiers[registration.tiers.length - 1];
    setCurrentTier(tier);

    // Expand first day by default on mobile
    if (window.innerWidth < 768 && schedule.length > 0) {
      setExpandedDays({ 0: true });
    }
  }, [dates.start, registration.tiers, schedule.length]);

  const toggleDay = (index) => {
    setExpandedDays(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const toggleFaq = (index) => {
    setExpandedFaq(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handlePrintSchedule = () => {
    window.print();
  };

  return (
    <div className="min-h-screen" data-testid="events-page">
      <style>{printStyles}</style>
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={images.hero}
            alt="St. Louis Skyline at Twilight"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d1b2e]/85 via-[#1b1435]/80 to-[#0d1b2e]/90"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center pt-24 pb-16">
          {/* Organization Crest/Seal - Only show if configured */}
          {images.crest && event.showCrest !== false && (
            <div className="mb-8">
              <img 
                src={images.crest}
                alt="Organization Seal"
                className="h-32 md:h-40 w-auto mx-auto drop-shadow-2xl"
              />
            </div>
          )}

          {/* Event Title */}
          <div className="mb-6">
            <p className="text-[#c9a24b] uppercase tracking-[0.3em] text-sm md:text-base font-semibold mb-4">
              Save the Date
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 tracking-tight">
              {event.name}
              <span className="block text-[#c9a24b]">{event.year}</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 italic font-light mb-4">
              &ldquo;{event.tagline}&rdquo;
            </p>
            {event.bibleVerse && (
              <div className="max-w-2xl mx-auto">
                <p className="text-white/80 text-sm md:text-base italic leading-relaxed">
                  "{event.bibleVerse}"
                </p>
                <p className="text-[#c9a24b] text-sm mt-2 font-medium">
                  — {event.bibleReference}
                </p>
              </div>
            )}
          </div>

          {/* Dates Display */}
          <div className="mb-10 mt-8">
            <div className="inline-flex items-center bg-[#3d1f4f]/40 backdrop-blur-sm rounded-full px-6 py-3 border border-[#c9a24b]/30">
              <Calendar className="w-5 h-5 text-[#c9a24b] mr-3" />
              <span className="text-white text-lg md:text-xl font-medium">{dates.displayText}</span>
            </div>
          </div>

          {/* Countdown */}
          {daysUntilEvent && daysUntilEvent > 0 && (
            <p className="text-white/70 text-sm mb-8">
              {daysUntilEvent} days until the conference
            </p>
          )}

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center no-print">
            <a href="#registration">
              <Button 
                size="lg" 
                className="bg-[#c9a24b] hover:bg-[#b8923a] text-[#0d1b2e] font-bold text-lg px-8 py-6 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300"
                data-testid="register-now-btn"
              >
                Register Now
              </Button>
            </a>
            <a href="#schedule">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white/50 text-white hover:bg-white/10 font-semibold text-lg px-8 py-6 rounded-lg backdrop-blur-sm"
              >
                View Schedule
              </Button>
            </a>
          </div>

          {/* Venue Quick Info */}
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 text-white/80 text-sm">
            <div className="flex items-center">
              <MapPin className="w-4 h-4 mr-2 text-[#c9a24b]" />
              <span>{venue.name}</span>
            </div>
            <span className="hidden sm:inline text-white/40">|</span>
            <div className="flex items-center">
              <Building className="w-4 h-4 mr-2 text-[#c9a24b]" />
              <span>{venue.city}, {venue.state}</span>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce no-print">
          <ChevronDown className="w-8 h-8 text-white/50" />
        </div>
      </section>

      {/* Event Overview Section */}
      <section id="overview" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2e] mb-6">
              About the Conference
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed whitespace-pre-line">
              {event.description}
            </p>
          </div>

          {/* Who Should Attend */}
          <div className="max-w-3xl mx-auto">
            <h3 className="text-xl font-semibold text-[#0d1b2e] mb-4 text-center">Who Should Attend</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {event.whoShouldAttend.map((item, index) => (
                <div key={`attend-${index}`} className="flex items-start">
                  <Check className="w-5 h-5 text-[#c9a24b] mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Venue Info Card */}
          <div className="mt-16 max-w-4xl mx-auto">
            <Card className="overflow-hidden shadow-lg border-0">
              <div className="bg-gradient-to-r from-[#0d1b2e] to-[#1b1435] p-6 text-white">
                <h3 className="text-2xl font-bold flex items-center">
                  <MapPin className="w-6 h-6 mr-3 text-[#c9a24b]" />
                  Conference Venue
                </h3>
              </div>
              <CardContent className="p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold text-[#0d1b2e] text-lg mb-2">{venue.name}</h4>
                    <p className="text-gray-600 mb-4">
                      {venue.address}<br />
                      {venue.city}, {venue.state} {venue.zip}
                    </p>
                    <p className="text-gray-600 flex items-center mb-4">
                      <Phone className="w-4 h-4 mr-2" />
                      {venue.phone}
                    </p>
                    <a 
                      href={venue.mapUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-[#007A7A] hover:text-[#005a5a] font-medium"
                    >
                      Get Directions <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                  </div>
                  <div className="bg-gray-100 rounded-lg h-48 md:h-auto overflow-hidden">
                    <iframe
                      src={venue.mapEmbed}
                      width="100%"
                      height="100%"
                      style={{ border: 0, minHeight: '200px' }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Venue Map"
                    ></iframe>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Schedule Section */}
      <section id="schedule" className="py-20 bg-[#f8f7f4] schedule-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12 max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2e]">
              Conference Schedule
            </h2>
            <Button 
              variant="outline" 
              onClick={handlePrintSchedule}
              className="no-print border-[#0d1b2e] text-[#0d1b2e] hover:bg-[#0d1b2e] hover:text-white"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>

          <div className="max-w-5xl mx-auto space-y-6">
            {schedule.map((day, dayIndex) => (
              <Card key={`day-${dayIndex}`} className="overflow-hidden shadow-md border-0">
                {/* Day Header - Clickable on mobile */}
                <button
                  onClick={() => toggleDay(dayIndex)}
                  className="w-full bg-gradient-to-r from-[#0d1b2e] to-[#1b1435] p-4 md:p-6 text-white text-left md:cursor-default flex justify-between items-center"
                  aria-expanded={expandedDays[dayIndex]}
                >
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold">{day.day}</h3>
                    <p className="text-white/80 text-sm md:text-base">{day.date}</p>
                  </div>
                  <ChevronDown 
                    className={`w-6 h-6 md:hidden transition-transform duration-300 ${expandedDays[dayIndex] ? 'rotate-180' : ''}`} 
                  />
                </button>

                {/* Sessions - Always visible on desktop, collapsible on mobile */}
                <div className={`${expandedDays[dayIndex] === false ? 'hidden md:block' : ''}`}>
                  <CardContent className="p-0">
                    <div className="divide-y divide-gray-100">
                      {day.sessions.map((session, sessionIndex) => (
                        <div 
                          key={`session-${dayIndex}-${sessionIndex}`}
                          className="p-4 md:p-5 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6">
                            <div className="flex items-center text-[#c9a24b] font-semibold min-w-[140px]">
                              <Clock className="w-4 h-4 mr-2" />
                              {session.time}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-[#0d1b2e]">{session.title}</h4>
                              <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-500">
                                {session.location && (
                                  <span className="flex items-center">
                                    <MapPin className="w-3 h-3 mr-1" />
                                    {session.location}
                                  </span>
                                )}
                                {session.speaker && (
                                  <span className="flex items-center">
                                    <Users className="w-3 h-3 mr-1" />
                                    {session.speaker}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Hotel & Travel Section */}
      <section id="hotel" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2e] mb-12 text-center">
            Hotel & Travel
          </h2>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Hotel Card */}
            <Card className="shadow-lg border-0 overflow-hidden">
              <div className="bg-[#3d1f4f] p-4 text-white">
                <h3 className="text-xl font-bold flex items-center">
                  <Building className="w-5 h-5 mr-2 text-[#c9a24b]" />
                  Host Hotel
                </h3>
              </div>
              <CardContent className="p-6">
                <h4 className="font-bold text-[#0d1b2e] text-lg mb-2">{hotel.name}</h4>
                <p className="text-gray-600 mb-4">{hotel.address}</p>
                
                <div className="bg-[#c9a24b]/10 rounded-lg p-4 mb-4">
                  <p className="font-semibold text-[#0d1b2e]">Group Rate: <span className="text-[#c9a24b]">{hotel.groupRate}</span></p>
                  {hotel.groupCode ? (
                    <p className="text-sm text-gray-600">Use code: <span className="font-mono font-bold">{hotel.groupCode}</span></p>
                  ) : (
                    <p className="text-sm text-green-600 font-medium">No code required — group rate automatically applied</p>
                  )}
                  {hotel.cutoffDate && (
                    <p className="text-sm text-red-600 mt-2">Book by {hotel.cutoffDate}</p>
                  )}
                </div>

                {/* Check-in/Check-out Times */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Check-in:</span>
                    <span className="font-semibold text-[#0d1b2e]">{hotel.checkIn}</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-gray-600">Check-out:</span>
                    <span className="font-semibold text-[#0d1b2e]">{hotel.checkOut}</span>
                  </div>
                </div>

                {/* Payment Note */}
                {hotel.paymentNote && (
                  <p className="text-sm text-gray-600 mb-3 italic">{hotel.paymentNote}</p>
                )}

                {/* Separate Cost Note */}
                {hotel.separateCostNote && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-amber-800 font-medium">{hotel.separateCostNote}</p>
                  </div>
                )}

                <div className="space-y-2 mb-4">
                  {hotel.amenities.map((amenity, index) => (
                    <div key={`amenity-${index}`} className="flex items-center text-sm text-gray-600">
                      <Check className="w-4 h-4 text-green-500 mr-2" />
                      {amenity}
                    </div>
                  ))}
                </div>

                <div className="flex flex-col gap-2">
                  <a 
                    href={hotel.bookingUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center bg-[#0d1b2e] text-white px-4 py-3 rounded-lg hover:bg-[#1b1435] transition-colors font-semibold"
                  >
                    Book Your Room <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                  <p className="text-center text-sm text-gray-500 flex items-center justify-center">
                    <Phone className="w-3 h-3 mr-1" />
                    {hotel.phone}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Travel Info Card */}
            <Card className="shadow-lg border-0 overflow-hidden">
              <div className="bg-[#3d1f4f] p-4 text-white">
                <h3 className="text-xl font-bold flex items-center">
                  <Plane className="w-5 h-5 mr-2 text-[#c9a24b]" />
                  Travel Information
                </h3>
              </div>
              <CardContent className="p-6 space-y-6">
                <div>
                  <h4 className="font-semibold text-[#0d1b2e] flex items-center mb-2">
                    <Plane className="w-4 h-4 mr-2 text-[#c9a24b]" />
                    Nearest Airport
                  </h4>
                  <p className="text-gray-600">{travel.nearestAirport}</p>
                  <p className="text-sm text-gray-500">{travel.airportDistance}</p>
                </div>

                <div>
                  <h4 className="font-semibold text-[#0d1b2e] flex items-center mb-2">
                    <Car className="w-4 h-4 mr-2 text-[#c9a24b]" />
                    Parking
                  </h4>
                  <p className="text-gray-600">{travel.parking}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 italic">{travel.additionalNotes}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Registration & Pricing Section */}
      <section id="registration" className="py-20 bg-gradient-to-b from-[#0d1b2e] to-[#1b1435] text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Registration & Pricing
          </h2>
          <p className="text-center text-white/70 mb-4">
            Full balance due by <span className="text-[#c9a24b] font-semibold">{registration.deadline}</span>
          </p>
          {registration.balanceNote && (
            <p className="text-center text-white/60 text-sm mb-2">{registration.balanceNote}</p>
          )}
          {registration.incrementalPaymentNote && (
            <p className="text-center text-white/60 text-sm mb-12">{registration.incrementalPaymentNote}</p>
          )}

          {/* Pricing Tiers */}
          <div className={`grid gap-6 max-w-4xl mx-auto mb-12 ${registration.tiers.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
            {registration.tiers.map((tier, index) => (
              <Card 
                key={`tier-${index}`}
                className={`relative overflow-hidden border-0 ${
                  currentTier?.name === tier.name 
                    ? 'ring-4 ring-[#c9a24b] shadow-2xl scale-105' 
                    : 'shadow-lg'
                }`}
              >
                {currentTier?.name === tier.name && (
                  <div className="absolute top-0 left-0 right-0 bg-[#c9a24b] text-[#0d1b2e] text-center py-1 text-sm font-bold">
                    Current Rate
                  </div>
                )}
                <CardContent className={`p-6 text-center ${currentTier?.name === tier.name ? 'pt-10' : ''}`}>
                  <h3 className="text-xl font-bold text-[#0d1b2e] mb-2">{tier.name}</h3>
                  <p className="text-3xl font-bold text-[#c9a24b] mb-1">${tier.price}</p>
                  {tier.savings && (
                    <p className="text-green-600 font-semibold text-sm mb-2">Save ${tier.savings}!</p>
                  )}
                  <p className="text-sm text-gray-500">{tier.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Deposit Info */}
          <div className="max-w-2xl mx-auto bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-12">
            <h4 className="font-semibold text-[#c9a24b] mb-2 flex items-center">
              <DollarSign className="w-5 h-5 mr-2" />
              Deposit Requirement
            </h4>
            <p className="text-white/90">
              A non-refundable deposit of <span className="font-bold">${registration.deposit.amount}</span> is required upon registration to secure your spot. {registration.deposit.terms}
            </p>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Link to="/donate">
              <Button 
                size="lg"
                className="bg-[#c9a24b] hover:bg-[#b8923a] text-[#0d1b2e] font-bold text-lg px-12 py-6 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300"
                data-testid="register-pay-btn"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Register & Pay Now
              </Button>
            </Link>
            <div className="mt-6 max-w-xl mx-auto">
              <div className="flex items-center justify-center bg-yellow-400 border-2 border-yellow-500 rounded-lg px-5 py-4 shadow-lg animate-pulse">
                <AlertCircle className="w-6 h-6 text-yellow-900 mr-3 flex-shrink-0" />
                <p className="text-yellow-900 text-base font-bold text-left">
                  <span className="uppercase">Important:</span> Select &ldquo;{payment.dropdownLabel}&rdquo; as your designation to complete the registration form.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Info Section */}
      <section id="payment" className="py-16 bg-[#f8f7f4]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-[#0d1b2e] mb-6 text-center">Payment Methods</h3>
            
            {/* Payment Method Icons */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <p className="text-gray-600 mb-6 text-center">We accept the following payment methods:</p>
              <div className="flex flex-wrap justify-center gap-4 mb-6">
                {/* PayPal */}
                <div className="flex flex-col items-center p-3 bg-gray-50 rounded-lg min-w-[100px]">
                  <svg className="w-10 h-10 mb-1" viewBox="0 0 24 24" fill="#003087">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z"/>
                  </svg>
                  <span className="text-xs text-gray-600 font-medium">PayPal</span>
                </div>
                
                {/* Western Union */}
                <div className="flex flex-col items-center p-3 bg-gray-50 rounded-lg min-w-[100px]">
                  <svg className="w-10 h-10 mb-1" viewBox="0 0 24 24" fill="#FFDD00">
                    <rect width="24" height="24" rx="4"/>
                    <text x="12" y="16" fontSize="8" fill="#000" textAnchor="middle" fontWeight="bold">WU</text>
                  </svg>
                  <span className="text-xs text-gray-600 font-medium">Western Union</span>
                </div>
                
                {/* Money Order */}
                <div className="flex flex-col items-center p-3 bg-gray-50 rounded-lg min-w-[100px]">
                  <svg className="w-10 h-10 mb-1" viewBox="0 0 24 24" fill="none" stroke="#0d1b2e" strokeWidth="1.5">
                    <rect x="2" y="5" width="20" height="14" rx="2"/>
                    <line x1="2" y1="10" x2="22" y2="10"/>
                    <line x1="6" y1="15" x2="12" y2="15"/>
                  </svg>
                  <span className="text-xs text-gray-600 font-medium">Money Order</span>
                </div>
                
                {/* MoneyGram */}
                <div className="flex flex-col items-center p-3 bg-gray-50 rounded-lg min-w-[100px]">
                  <svg className="w-10 h-10 mb-1" viewBox="0 0 24 24" fill="#FF6600">
                    <circle cx="12" cy="12" r="10"/>
                    <text x="12" y="16" fontSize="7" fill="#fff" textAnchor="middle" fontWeight="bold">MG</text>
                  </svg>
                  <span className="text-xs text-gray-600 font-medium">MoneyGram</span>
                </div>
                
                {/* Check */}
                <div className="flex flex-col items-center p-3 bg-gray-50 rounded-lg min-w-[100px]">
                  <svg className="w-10 h-10 mb-1" viewBox="0 0 24 24" fill="none" stroke="#0d1b2e" strokeWidth="1.5">
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <line x1="6" y1="9" x2="18" y2="9"/>
                    <line x1="6" y1="13" x2="14" y2="13"/>
                    <line x1="6" y1="17" x2="10" y2="17"/>
                  </svg>
                  <span className="text-xs text-gray-600 font-medium">Check</span>
                </div>
              </div>
            </div>

            {/* Mailing Address */}
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <p className="font-semibold text-[#0d1b2e] mb-3">Mail Payments To:</p>
              <p className="text-gray-600 mb-2">
                Make checks/money orders payable to <span className="font-semibold">{payment.checkPayable}</span>
              </p>
              <div className="bg-[#f8f7f4] rounded-lg p-4 inline-block">
                <p className="text-[#0d1b2e] font-mono text-lg">{payment.mailingAddress}</p>
              </div>
              <p className="text-sm text-gray-500 mt-4 italic">
                Please include your name and &ldquo;ARGA Conference 2027&rdquo; on the memo line.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2e] mb-12 text-center">
            Frequently Asked Questions
          </h2>

          <div className="max-w-3xl mx-auto space-y-4">
            {faq.map((item, index) => (
              <Card key={`faq-${index}`} className="border border-gray-200 shadow-sm overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full p-5 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                  aria-expanded={expandedFaq[index]}
                >
                  <span className="font-semibold text-[#0d1b2e] pr-4 flex items-start">
                    <HelpCircle className="w-5 h-5 mr-3 text-[#c9a24b] flex-shrink-0 mt-0.5" />
                    {item.question}
                  </span>
                  {expandedFaq[index] ? (
                    <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq[index] && (
                  <div className="px-5 pb-5 pl-12 text-gray-600 leading-relaxed">
                    {item.answer}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 bg-[#3d1f4f] text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">Questions?</h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            For any questions about the conference, registration, or accommodations, please reach out to us.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {contact.emails && contact.emails.map((email, index) => (
              <a 
                key={`contact-email-${index}`}
                href={`mailto:${email}?subject=${encodeURIComponent(contact.subject)}`}
                className="inline-flex items-center bg-[#c9a24b] text-[#0d1b2e] px-6 py-3 rounded-lg font-semibold hover:bg-[#b8923a] transition-colors"
              >
                <Mail className="w-5 h-5 mr-2" />
                {email}
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Events;
